import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Tab,
  Tabs,
  Skeleton,
  Divider,
  Stack,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";

interface CrewMember {
  id: number;
  name: string;
  avatar?: string;
  averageRating: number;
  totalReviews: number;
  roles: string[];
}

const fetchTopRated = async (): Promise<CrewMember[]> => {
  return new Promise((res) =>
    setTimeout(
      () =>
        res([
          {
            id: 65,
            name: "Timothy Mutede",
            averageRating: 5.0,
            totalReviews: 12,
            roles: ["Camera", "Live Switcher"],
          },
          {
            id: 2,
            name: "Foster Jumbe",
            averageRating: 4.9,
            totalReviews: 8,
            roles: ["Networking Engineer"],
          },
          {
            id: 30,
            name: "Tinotenda Chibaya",
            averageRating: 4.8,
            totalReviews: 10,
            roles: ["Camera"],
          },
          {
            id: 75,
            name: "Maxwell Manyanhaire",
            averageRating: 4.56,
            totalReviews: 9,
            roles: ["Live Switcher", "Networking Engineer"],
          },
          {
            id: 12,
            name: "Leon Manhimanzi",
            averageRating: 4.4,
            totalReviews: 6,
            roles: ["Camera"],
          },
        ]),
      800,
    ),
  );
};

const fetchLowRated = async (): Promise<CrewMember[]> => {
  return new Promise((res) =>
    setTimeout(
      () =>
        res([
          {
            id: 74,
            name: "Nigel Tavaguta",
            averageRating: 3.4,
            totalReviews: 5,
            roles: ["Networking Engineer"],
          },
          {
            id: 19,
            name: "Jon Jones",
            averageRating: 2.8,
            totalReviews: 4,
            roles: ["Camera"],
          },
          {
            id: 33,
            name: "Jaheed Osman",
            averageRating: 2.2,
            totalReviews: 3,
            roles: ["Live Switcher"],
          },
        ]),
      800,
    ),
  );
};

// ─── Star renderer ────────────────────────────────────────────────────────────

const StarRating = ({ value, color }: { value: number; color: string }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (value >= i) {
      stars.push(<StarIcon key={i} sx={{ fontSize: 16, color }} />);
    } else if (value >= i - 0.5) {
      stars.push(<StarHalfIcon key={i} sx={{ fontSize: 16, color }} />);
    } else {
      stars.push(
        <StarBorderIcon key={i} sx={{ fontSize: 16, color: "#ccc" }} />,
      );
    }
  }
  return <Box sx={{ display: "flex", alignItems: "center" }}>{stars}</Box>;
};

// ─── Medal colours for top 3 ──────────────────────────────────────────────────

const medalColour = (index: number) =>
  index === 0
    ? "#FFB300"
    : index === 1
      ? "#90A4AE"
      : index === 2
        ? "#A1674A"
        : null;

// ─── Single row ───────────────────────────────────────────────────────────────

const CrewRow = ({
  member,
  index,
  mode,
}: {
  member: CrewMember;
  index: number;
  mode: "top" | "low";
}) => {
  const medal = mode === "top" ? medalColour(index) : null;
  const ratingColor =
    mode === "top"
      ? member.averageRating >= 4.5
        ? "#2e7d32"
        : "#f57c00"
      : member.averageRating <= 2
        ? "#c62828"
        : "#e65100";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        transition: "background 0.15s",
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
    >
      {/* Rank / medal */}
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: medal
            ? medal
            : mode === "low"
              ? "#fdecea"
              : "#eeeeee",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: medal ? "#fff" : mode === "low" ? "#c62828" : "#616161",
          }}
        >
          {index + 1}
        </Typography>
      </Box>

      {/* Avatar */}
      <Avatar
        src={member.avatar}
        sx={{
          width: 40,
          height: 40,
          border: medal ? `2px solid ${medal}` : "2px solid #e0e0e0",
          flexShrink: 0,
        }}
      >
        {member.name.charAt(0)}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ color: "#1a1a1a" }}
        >
          {member.name}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.3}>
          {member.roles.map((r) => (
            <Chip
              key={r}
              label={r}
              size="small"
              sx={{
                fontSize: 10,
                height: 18,
                backgroundColor: "#eeeeee",
                color: "#555",
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Rating */}
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: ratingColor,
            lineHeight: 1,
          }}
        >
          {member.averageRating.toFixed(1)}
        </Typography>
        <StarRating value={member.averageRating} color={ratingColor} />
        <Typography sx={{ fontSize: 10, color: "#999", mt: 0.3 }}>
          {member.totalReviews} review{member.totalReviews !== 1 ? "s" : ""}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingRows = () => (
  <>
    {[1, 2, 3].map((i) => (
      <Box
        key={i}
        sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5, px: 2 }}
      >
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={12} sx={{ mt: 0.5 }} />
        </Box>
        <Box>
          <Skeleton width={36} height={24} />
          <Skeleton width={80} height={14} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
    ))}
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CrewRatings: React.FC = () => {
  const [tab, setTab] = useState<0 | 1>(0);
  const [topCrew, setTopCrew] = useState<CrewMember[]>([]);
  const [lowCrew, setLowCrew] = useState<CrewMember[]>([]);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingLow, setLoadingLow] = useState(true);

  useEffect(() => {
    fetchTopRated().then((data) => {
      setTopCrew(data);
      setLoadingTop(false);
    });
    fetchLowRated().then((data) => {
      setLowCrew(data);
      setLoadingLow(false);
    });
  }, []);

  const isTop = tab === 0;
  const crew = isTop ? topCrew : lowCrew;
  const loading = isTop ? loadingTop : loadingLow;

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 3,
        overflow: "hidden",
        maxWidth: 520,
        mt: 2,
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: isTop ? "#fff8e1" : "#fdecea",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.3s",
            }}
          >
            {isTop ? (
              <EmojiEventsIcon sx={{ color: "#FFB300", fontSize: 22 }} />
            ) : (
              <WarningAmberIcon sx={{ color: "#e53935", fontSize: 22 }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
            >
              Crew Ratings
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isTop ? "Top performers this month" : "Crew needing attention"}
            </Typography>
          </Box>
        </Box>

        {/* Live dot */}
        <Chip
          size="small"
          label="This Month"
          sx={{
            fontSize: 10,
            height: 20,
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
          }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 2,
          mt: 1.5,
          "& .MuiTab-root": {
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
            minHeight: 36,
            py: 0,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: tab === 0 ? "#FFB300" : "#e53935",
          },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <EmojiEventsIcon sx={{ fontSize: 14 }} />
              Top Rated
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              Low Ratings
            </Box>
          }
        />
      </Tabs>

      <Divider />

      {/* Body */}
      <CardContent sx={{ p: 1, pb: "8px !important" }}>
        {loading ? (
          <LoadingRows />
        ) : crew.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2">
              No crew in this category yet.
            </Typography>
          </Box>
        ) : (
          crew.map((member, i) => (
            <React.Fragment key={member.id}>
              <CrewRow member={member} index={i} mode={isTop ? "top" : "low"} />
              {i < crew.length - 1 && <Divider sx={{ mx: 2, opacity: 0.4 }} />}
            </React.Fragment>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default CrewRatings;
